import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUserApi } from "../../../_common/hooks/actions/user/appUserApiHook";
import { URLS } from "../../../_config";

interface VerificationState {
  loading: boolean;
  success: boolean;
  error: string | null;
}

interface ResendFormValues {
  email: string;
}

const resendSchema = yup.object({
  email: yup.string().email("Please enter a valid email address").required("Email is required"),
});

export default function VerifyEmail() {
  const history = useHistory();
  const location = useLocation();
  const { callVerifyEmail, callResendVerification } = useUserApi();

  const [verificationState, setVerificationState] = useState<VerificationState>({
    loading: true,
    success: false,
    error: null,
  });

  const [showResendForm, setShowResendForm] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResendFormValues>({
    resolver: yupResolver(resendSchema),
    defaultValues: {
      email: "",
    },
  });

  // Extract token from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get("token");

  useEffect(() => {
    // if (!token) {
    //   setVerificationState({
    //     loading: false,
    //     success: false,
    //     error: "Invalid verification link. No token provided.",
    //   });
    //   return;
    // }
    console.log("🔎 [SAGA] BEFORE verifying email:", token);
    if (token) {
      // Call verify email API
      handleEmailVerification(token);
    }
  }, [token]);

  const handleEmailVerification = (verificationToken: string) => {
    setVerificationState({
      loading: true,
      success: false,
      error: null,
    });

    const onSuccess = (response: any) => {
      console.log("Email verification success:", response);
      setVerificationState({
        loading: false,
        success: true,
        error: null,
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        history.push(URLS.AUTH.LOGIN + "?verified=true");
      }, 3000);
    };

    const onError = (error: any) => {
      console.log("Email verification error:", error);
      setVerificationState({
        loading: false,
        success: true,
        error: null,
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        history.push(URLS.AUTH.LOGIN + "?verified=true");
      }, 3000);
    };

    callVerifyEmail({ token: verificationToken }, onSuccess, onError);
  };

  const handleRetryVerification = () => {
    // if (token) {
    //   handleEmailVerification(token);
    // }
    window.location.reload();
  };

  const handleGoToLogin = () => {
    history.push(URLS.AUTH.LOGIN);
  };

  const handleShowResendForm = () => {
    setShowResendForm(true);
  };

  const handleResendSubmit = (data: ResendFormValues) => {
    setResendLoading(true);

    const onSuccess = (response: any) => {
      console.log("Resend verification success:", response);
      setResendLoading(false);
      setShowResendForm(false);
      reset();
      setVerificationState({
        loading: false,
        success: false,
        error: "Verification email sent successfully! Please check your email.",
      });
    };

    const onError = (error: any) => {
      setResendLoading(false);

      // Extract error message from API response
      let errorMessage = "Failed to send verification email. Please try again.";
      if (error) {
        if (typeof error === "string") {
          errorMessage = error;
        } else if (error.error) {
          errorMessage = error.error;
        } else if (error.errors) {
          errorMessage = Array.isArray(error.errors) ? error.errors.join(", ") : error.errors;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.data && error.data.error) {
          errorMessage = error.data.error;
        } else if (error.data && error.data.message) {
          errorMessage = error.data.message;
        }
      }

      setVerificationState({
        loading: false,
        success: false,
        error: errorMessage,
      });
    };

    callResendVerification(data, onSuccess, onError);
  };

  return (
    <div className="verify-email-main">
      <div className="verify-email-container">
        <div className="verify-email-card">
          {/* Header */}
          <div className="verify-email-header">
            <div className="logo">
              <img src="/images/dr-logo.png" alt="DrJob" className="logo-image" />
            </div>
          </div>

          {/* Content */}
          <div className="verify-email-content">
            {verificationState.loading && (
              <div className="verification-loading">
                <div className="loading-spinner"></div>
                <h2 className="verification-title">Verifying Your Email...</h2>
                <p className="verification-message">Please wait while we verify your email address.</p>
              </div>
            )}

            {verificationState.success && (
              <div className="verification-success">
                <div className="success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#10B981" />
                    <path
                      d="M9 12l2 2 4-4"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="verification-title">Email Verified Successfully!</h2>
                <p className="verification-message">
                  Your email has been verified successfully. You can now log in to your account.
                </p>
                <p className="redirect-message">Redirecting to login page in 3 seconds...</p>
                <button onClick={handleGoToLogin} className="btn-preview">
                  Go to Login Now
                </button>
              </div>
            )}

            {verificationState.error && (
              <div className="verification-error">
                <div className="error-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#EF4444" />
                    <path
                      d="M15 9l-6 6M9 9l6 6"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="verification-title">Verification Failed</h2>
                <p className="verification-message">{verificationState.error}</p>

                {showResendForm && (
                  <div className="resend-form">
                    <form onSubmit={handleSubmit(handleResendSubmit)}>
                      <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <Controller
                          name="email"
                          control={control}
                          render={(renderProps) => (
                            <input
                              onChange={renderProps.onChange}
                              onBlur={renderProps.onBlur}
                              value={renderProps.value}
                              name="email"
                              type="email"
                              placeholder="Enter your email address"
                              className={`form-input ${errors.email ? "error" : ""}`}
                            />
                          )}
                        />
                        {errors.email && <span className="error-text">{errors.email.message}</span>}
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          onClick={() => setShowResendForm(false)}
                          className="btn-cancel"
                          disabled={resendLoading}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn-preview" disabled={resendLoading}>
                          {resendLoading ? "Sending..." : "Send Verification Email"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {!showResendForm && (
                  <div className="error-actions">
                    {token && (
                      <button onClick={handleRetryVerification} className="btn-cancel">
                        Retry Verification
                      </button>
                    )}
                    {/* <button 
                      onClick={handleShowResendForm}
                      className="btn-cancel"
                    >
                      Resend Verification Link
                    </button> */}
                    <button onClick={handleGoToLogin} className="btn-preview">
                      Go to Login
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="verify-email-footer">
            <p>&copy; 2025, Dr. Job FZ LLC. All Rights Reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
